import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Ruler, Calculator, Layers, Printer, Save, Palette, ArrowRight, Check, Sparkles,
  Camera, Home, Store, Building2, Brush, Scissors,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Seo } from "../components/Seo";

const HERO_IMG = "/landing-hero-blue.webp";

const AUDIENCE = [
  { icon: Camera, label: "Artists & photographers", note: "Selling framed work without pro software" },
  { icon: Home, label: "Serious home framers", note: "Frame like a professional, at home" },
  { icon: Brush, label: "Novice hobby framers", note: "Learn the numbers as you go" },
  { icon: Building2, label: "Small studios", note: "Fast, repeatable job specs" },
  { icon: Store, label: "Etsy sellers", note: "Consistent products with minimal cost" },
  { icon: Scissors, label: "Custom framers & artisans", note: "Precision cut sheets every time" },
];

const FEATURES = [
  { icon: Calculator, title: "Solve it either way", body: "Know your artwork? Get the frame size. Know your frame? Get the artwork size. The maths runs both directions." },
  { icon: Ruler, title: "Exact cut sizes", body: "Matboard and backing board dimensions cut to the millimetre. Glass sized to fit the rebate without binding." },
  { icon: Layers, title: "Single, double & triple mats", body: "Select individual under-mat reveals and choose colours per layer, with a live cross-section of the whole stack." },
  { icon: Palette, title: "Aesthetic rules built in", body: "Choose equal, weighted-bottom or golden-ratio borders to create the gallery looks framers reach for, done for you." },
  { icon: Printer, title: "Printable cut sheet", body: "Take a clean, printable cut sheet to the bench so every measurement is right in front of you." },
  { icon: Save, title: "Save & reuse jobs", body: "Name jobs by client or artwork and reload them any time — perfect for repeat sizes and commissions." },
];

const PAINS = [
  { q: "Spent 20 minutes calculating… still not confident?", a: "Rebates, reveals, overlaps, glazing clearance - one slip and the mat is scrap. Get every cut size in seconds and know it's right before the blade touches the board." },
  { q: "Why does framing feel harder than making the art?", a: "Because the maths is fiddly and unforgiving. Framer's Bench does it for you, so finishing a piece feels as good as creating it." },
  { q: "Stop wasting matboard and mouldings.", a: "Every wrong measurement ends up in the offcut bin. Precise matboard, backing, glass and moulding sizes mean perfect cuts the first time - and less money thrown away." },
];

const FAQS = [
  { q: "Why doesn't my frame fit after cutting?", a: "The most common cause is confusing the artwork size, the visible window opening and the actual cut size. Your matboard and glass must be cut to the frame's rebate (rabbet) opening — slightly larger than the visible window — and glass is usually cut about 0.5 mm smaller so it doesn't bind. Framer's Bench calculates all three sizes separately so the wrong number never ends up on your cutter." },
  { q: "Do I need to install anything?", a: "No. Framer's Bench runs in your browser on any device and installs as an app if you want it on your home screen. The live calculator and preview are free to use — create a free account to save your first calculation, no card required." },
];

const Chip = ({ children }) => (
  <span className="inline-flex items-center rounded-full border border-border bg-white px-3 py-1.5 text-sm font-medium text-foreground/80">{children}</span>
);

export default function Landing() {
  return (
    <>
      <Seo title="Precision Frame & Mat Calculator for Artists & Framers" path="/" faq={FAQS} image={HERO_IMG}
        description="Stop wasting matboard and mouldings. Framer's Bench gives artists and framers exact mat, glass, backing and frame cut sizes on one easy-to-read page - perfect cuts the first time." />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="drafting-grid absolute inset-0 opacity-40" aria-hidden />
        <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:px-8 lg:py-24">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.06em] text-primary">
              <Sparkles className="h-3.5 w-3.5" /> Measure once · cut once · minimise wastage
            </span>
            <h1 className="mt-5 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" data-testid="hero-headline">
              Framing materials are expensive.{" "}
              <span className="text-primary">If you avoid just ONE mistake, this tool has paid for itself.</span>
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              One mis-cut mat or short length of moulding costs more than a month of Framer's Bench. Enter your
              artwork, choose a mat style, and get exact matboard, glass, backing and frame cut sizes in seconds -
              so you reach the cutter confident, work faster and stop throwing materials in the bin.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button asChild size="lg" className="gap-2" data-testid="hero-cta-calculator">
                <Link to="/calculator">Try the calculator free <ArrowRight className="h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" data-testid="hero-cta-pricing">
                <Link to="/pricing">See plans</Link>
              </Button>
            </div>
            <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
              <Check className="h-4 w-4 text-primary" /> Every calculation on one single, easy-to-read page. 7-day free trial, no card needed.
            </p>
          </div>
          <div className="relative">
            <div className="rotate-1 rounded-2xl border-[10px] border-white bg-white p-2 shadow-2xl ring-1 ring-border">
              <img src={HERO_IMG} alt="A gallery wall of framed artwork" className="h-full w-full rounded-lg object-cover" style={{ aspectRatio: "4/3" }} />
            </div>
          </div>
        </div>
      </section>

      {/* Pain points */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8" data-testid="pain-points">
        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-primary">Sound familiar?</p>
        <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3">
          {PAINS.map((p) => (
            <div key={p.q} className="rounded-2xl border border-border bg-card p-6">
              <h3 className="font-heading text-xl font-extrabold leading-snug tracking-tight">{p.q}</h3>
              <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">{p.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Who it's for */}
      <section className="border-y border-border bg-secondary/40">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">For artists who don't have time for maths - but can't afford mistakes.</h2>
          <p className="mt-3 max-w-2xl text-muted-foreground">Whether you frame one piece a month or fifty a week, Framer's Bench gives you the numbers fast, so your time goes into the work - not the arithmetic.</p>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {AUDIENCE.map(({ icon: Icon, label, note }) => (
              <div key={label} className="flex items-start gap-4 rounded-xl border border-border bg-card p-5">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><Icon className="h-5 w-5" /></div>
                <div>
                  <p className="font-heading text-base font-bold">{label}</p>
                  <p className="mt-0.5 text-sm text-muted-foreground">{note}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-y border-border bg-secondary/40">
        <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">Three steps to a perfect frame</h2>
          <div className="mt-10 grid grid-cols-1 gap-8 md:grid-cols-3">
            {[
              { n: "01", t: "Enter your sizes", d: "Type your artwork dimensions (or the frame you already have) in mm or inches." },
              { n: "02", t: "Choose your look", d: "Pick single, double or triple mats and an aesthetic rule — see it update live." },
              { n: "03", t: "Cut with confidence", d: "Read off exact matboard, glass and frame cut sizes, then print your cut sheet." },
            ].map((s) => (
              <div key={s.n}>
                <div className="font-heading text-4xl font-extrabold text-primary/30">{s.n}</div>
                <h3 className="mt-2 font-heading text-lg font-bold">{s.t}</h3>
                <p className="mt-1.5 text-[15px] leading-relaxed text-muted-foreground">{s.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Button asChild size="lg" className="gap-2" data-testid="how-cta-calculator">
              <Link to="/calculator">Open the calculator <ArrowRight className="h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <h2 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">Every calculation on one single, easy-to-read page</h2>
        <p className="mt-3 max-w-2xl text-muted-foreground">No spreadsheets, no scribbled notes, no second-guessing. Everything you need at the bench, in one place.</p>
        <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map(({ icon: Icon, title, body }) => (
            <div key={title} className="rounded-xl border border-border bg-card p-6">
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary"><Icon className="h-5 w-5" /></div>
              <h3 className="font-heading text-lg font-bold">{title}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA band */}
      <section className="border-t border-border bg-primary">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 px-4 py-12 text-center sm:px-6 lg:flex-row lg:px-8 lg:text-left">
          <div>
            <h2 className="font-heading text-2xl font-extrabold text-primary-foreground sm:text-3xl">Stop wasting matboard and mouldings. Get perfect cuts the first time.</h2>
            <p className="mt-2 text-primary-foreground/80">Try the calculator free, then start a 7-day trial — no card required. Subscribe from A$6.99/month - less than the cost of one spoiled mat.</p>
          </div>
          <div className="flex gap-3">
            <Button asChild size="lg" variant="secondary" data-testid="band-cta-calculator"><Link to="/calculator">Start free</Link></Button>
            <Button asChild size="lg" variant="outline" className="border-white/30 bg-transparent text-primary-foreground hover:bg-white/10 hover:text-primary-foreground" data-testid="band-cta-pricing"><Link to="/pricing">View pricing</Link></Button>
          </div>
        </div>
      </section>
    </>
  );
}
